import type { IssueSource } from './ownership';
import type { CostBasis, DeliverySnapshot, IssueStatus, Signatory, SignoffStageMode } from './types';
import { richHasText, type RichDoc } from './rich-text';
import { canOpen, type GroupsOf } from './groups';
// Type only, so the value-level cycle (phases.ts imports PROJECT_TYPE_GROUPS from
// here) is erased at runtime and never actually loads phases.ts from this module.
import type { PhaseDraft } from './phases';

/**
 * A project: one board's worth of work, and the people who may see it.
 *
 * Until now there was exactly one, kept in a single document called 'main'.
 * That document becomes the first project, keeping its id, so nothing that
 * already points at it has to be rewritten.
 *
 * Issues live UNDER their project rather than in one pile with a label,
 * because a permission rule can then tell which project a document belongs to
 * from its path alone — without fetching the issue first to find out. Every
 * lesson in this codebase about rules has been that a check needing a second
 * lookup is a check that cannot be proved.
 */

/**
 * AN APPROVED CHANGE TO THE CONTRACT VALUE — a scope adjustment (0153, #435).
 *
 * One entry per approved change: the old and new contract value, the reason, and
 * who approved it when. A manager/owner approval is the decision-tier default,
 * recorded and attributed; a signed variation document may optionally be
 * referenced where the governance warrants it. The list rides inside the
 * `contract_adjustments` jsonb on the board.
 */
export interface ContractAdjustment {
  /** A stable id for the entry. */
  id: string;
  /** The contract value before this change. */
  from: number;
  /** The approved new contract value. */
  to: number;
  /** Why the scope changed — required; this is the record of the decision. */
  reason: string;
  /** The manager or owner who approved the change (their email). */
  by: string;
  /** When it was approved, in milliseconds. */
  at: number;
  /** The budget baseline this change set. Equals `to` unless a deliberate
   *  contingency was kept (the baseline held apart from the contract) — #435 B. */
  baseline?: number;
  /** True when an admin recorded this without being the designated approver — a
   *  documented separation-of-duties exception (soft SoD). Surfaced as a control
   *  exception, mirroring the invoice override. */
  sodOverride?: boolean;
  /** Optional reference to a signed variation document. */
  documentRef?: string;
}

/** What a project's go-live is anchored to (0217). `phase`/`milestone` carry the id of the
 *  thing the go-live rides on (its planned date is derived from it); `fixed` is a committed
 *  typed date with no anchor. See {@link Project.goLiveAnchor}. */
export type GoLiveAnchorKind = 'phase' | 'milestone' | 'fixed';
export interface GoLiveAnchor {
  kind: GoLiveAnchorKind;
  /** The phase or milestone id for `phase`/`milestone`; absent for `fixed`. */
  id?: string;
}

export interface Project {
  id: string;
  name: string;
  key: string; // short prefix, e.g. ORB
  nextNumber: number;
  /** How card numbers are written. Absent = '#' and no padding. See ProjectMeta. */
  refPrefix?: string;
  refWidth?: number;
  source?: IssueSource;
  sourceName?: string;
  repoUrl?: string;
  /** Whether work is grouped into builds or sprints. Names things, nothing more. */
  cadence?: 'builds' | 'sprints';
  /** Whether the board is run as a bounded project or a continuous service — #435.
      Absent reads as project; see ProjectMeta.deliveryMode and src/lifecycle.ts. */
  deliveryMode?: 'project' | 'continuous';
  /** How the vendor(s) are paid — fixed price / time & materials / mixed (0219).
      Absent = unset = the balanced cost read; see src/portfolio.ts costBasisRead. */
  costBasis?: CostBasis;
  /** Whether the board shows the optional "In Staging" column — #471.
      Absent/false reads as off; see ProjectMeta.stagingEnabled and src/lifecycle.ts. */
  stagingEnabled?: boolean;
  createdAt?: number;
  archived?: boolean;
  /** A read-only worked EXAMPLE board (the Example Co. sample pair), set only by
   *  the sample seed — never by a person. Read-only is enforced in the app (the
   *  viewer's effective role is forced to "viewer" on a sample board, with a
   *  banner and a way back). Absent/false on every real board. The picker lists
   *  these apart, under a "Sample · read only" band. Column `sample` on `boards`
   *  (migration 0187). */
  sample?: boolean;
  /**
   * Who may open this project, when that is narrower than the workspace.
   *
   * EMPTY OR ABSENT MEANS EVERYONE, and that is not a shortcut — it is the only
   * safe default. One set of rules governs the real board and the test copy
   * together, and every project that exists today has no list. Reading an
   * absent list as "nobody" would lock every person out of every board the
   * moment this shipped.
   *
   * So a project is open until somebody names anyone on it. Name one person and
   * it is theirs, plus any workspace admin — who are never excluded, or an
   * admin could hand a project away and lose the ability to take it back.
   */
  members?: string[];
  /**
   * WHICH GROUPS MAY OPEN IT, alongside the people named above.
   *
   * Either list admits somebody; a board is reserved once EITHER has anything
   * in it. Reading only `members` would call a board named on a group and
   * nobody "open to everyone" — a board meant for one team, readable by the
   * whole workspace, with nothing on screen saying so.
   */
  groups?: string[];
  /**
   * WHOSE BOARD THIS IS. The email of whoever started it.
   *
   * WHY IT IS NOT COVERED BY ROLES. A role is held once, across the workspace:
   * `roles/{email}` holds one value, and `rank()` in firestore.rules reads that
   * one value whichever board is being touched. So making somebody a manager so
   * they can run their own board would make them a manager of every board they
   * can open — including the shared Sandbox and FlightDeck ones. The right that
   * is wanted here is narrower than any rung on that ladder: full run of ONE
   * board and nothing beyond it.
   *
   * ABSENT MEANS NOBODY OWNS IT, which is every board that existed before this,
   * and they carry on being run by rank alone. Ownership only ever adds; it
   * cannot take a right away from a manager or an admin.
   */
  owner?: string;

  /*
   * A PROJECT'S OWN DEFINITION — #387, migration 0091.
   *
   * What the create-project wizard captures beyond a name and a key. Every one
   * is optional, because "Start blank" is a supported way to make a project and
   * every board that predates this has none of it. Dates are milliseconds, the
   * way every date in this app is.
   */
  description?: RichDoc | null;
  startDate?: number;
  goLive?: number;
  /**
   * HOW THE GO-LIVE IS ANCHORED TO THE PLAN — 0217 (project-3311-forecast-witness-fixes).
   *
   * `goLive` above is the planned launch date. On its own it floats free of the plan and can
   * silently contradict it (a phase scheduled to end after it). This anchors it to the work
   * that delivers it, so its date and forecast come from the plan:
   *   · `{ kind: 'phase', id }`      the end of that phase — the planned date is DERIVED from the
   *                                  phase's target end (goLiveAnchorDay), so moving the phase
   *                                  moves the go-live and a contradiction is impossible.
   *   · `{ kind: 'milestone', id }`  the go-live IS that milestone (a committed/dated checkpoint).
   *   · `{ kind: 'fixed' }`          today's behaviour: the typed `goLive` is held as a committed
   *                                  date, not anchored; the planned-breach check still applies.
   * Absent → not yet anchored: a board with a typed `goLive` reads as a 'fixed' committed date
   * (with a nudge to anchor it); a board with neither shows the unset state. Derivation lives in
   * src/phases.ts (goLiveAnchorDay) and src/milestones.ts (milestoneScope for the go-live).
   */
  goLiveAnchor?: GoLiveAnchor;
  /**
   * THE REAL GO-LIVE, FROZEN AT THE HANDOFF — #435 surface 2, migration 0192.
   *
   * `goLive` above is the PLANNED date; this is the moment the project was marked
   * live and moved to service. Absent until the handoff — and its presence is what
   * makes the board read "in service" (see src/lifecycle.ts boardLifecycleTier and
   * handoff.ts). Editable at confirm (a team often marks it after the fact), so it
   * is never auto-stamped. Milliseconds, like every date here.
   */
  actualGoLive?: number;
  /**
   * THE AS-DELIVERED RECORD, FROZEN AT THE HANDOFF — #435 surface 2, migration 0192.
   *
   * What was delivered against what was planned, snapshotted the instant the
   * project goes live so a later re-plan or rename cannot rewrite it — the same
   * freeze AcceptanceEvidence does for a phase acceptance. Survives a revert to
   * project, because it is the record of what shipped. Rides as jsonb.
   */
  deliverySnapshot?: DeliverySnapshot;
  priority?: ProjectPriority;
  /** What kind of work this is; seeds the phases at creation, then a label. */
  projectType?: string;

  /*
   * CHARTER SUMMARY — the figures a portfolio (#385) and a cost view (#384)
   * read at a glance. The full charter is uploaded as a document (#380); this
   * is deliberately the summary, not the document.
   */
  budget?: number;
  /**
   * THE AGREED CONTRACT VALUE — the total the budget is based on (0152, #435).
   *
   * Vinnie, 8 Oct 2026: contract value "rolls into the budget — that is how we
   * base our budget." So this is the agreed external total; the `budget`
   * baseline above is anchored to it (auto-populated on entry, still editable).
   * OPTIONAL: an internal, non-vendor project has a budget but no contract, and
   * sets its baseline directly. A later change is a signed-off scope adjustment
   * (the baseline follows on sign-off). Renders through fmtMoney everywhere.
   */
  contractValue?: number;
  /**
   * THE LOG OF APPROVED CHANGES TO THE CONTRACT VALUE — scope adjustments (0153).
   *
   * Changing the contract value on a project that already has one is a decision,
   * not an edit: a manager or owner approves it with a reason, and the record —
   * who, when, old→new, why — is kept here and written to the audit trail as a
   * decision line (like approving a payment). The budget baseline follows an
   * approved change. First-time setting writes no entry. Rides as jsonb.
   */
  contractAdjustments?: ContractAdjustment[];
  /**
   * NUDGE WHEN A PAYMENT IS APPROVED BELOW ITS DELIVERED SHARE — #483.
   *
   * A board can ask to be warned when someone approves a payment while the work
   * it pays for is less than `pct`% delivered. It is a NUDGE, never a block: the
   * approver sees the shortfall, can still approve, and the fact that they did is
   * recorded on the line (approvedBelowDelivery). Off by default — a board opts in
   * and sets the bar. Rides as jsonb; shape `{ on, pct }`.
   */
  deliveryNudge?: { on: boolean; pct: number };
  fundingSource?: string;
  /*
   * HOW THE BUDGET IS CLASSIFIED AND TRACKED — migration 0108 (PM Witness).
   *
   * `budget` above is the approved baseline total. These say how it is
   * classified and how it is tracking, the two things a sponsor actually reads:
   *
   *   capex/opex — the split a sponsor and finance approve from different pools
   *     and watch as cloud/SaaS shifts capital spend to operating spend. A
   *     TWO-AMOUNT split (not a single toggle: most ICT projects are genuinely
   *     mixed — hardware/licences capitalised, implementation/subscriptions
   *     expensed), each with an optional one-line note. They sum TOWARD the
   *     baseline; we nudge, never block, when they do not reconcile.
   *   actualSpend / forecast — SUPERSEDED, in part. `forecast` (estimate-at-
   *     completion) is still the hand-set number the verdict reads. `actualSpend`
   *     was a single hand-typed spent-to-date figure; the budget ledger (0116,
   *     `invoices` below) now DERIVES actual spend as the sum of paid lines, so
   *     `actualSpend` is no longer written or shown. The column stays for older
   *     rows; nothing reads it. Variance (forecast − baseline) is DERIVED for
   *     display, never stored — the budget line can say "on track / over".
   *
   * All optional numbers (numeric in Postgres, so they are in NUMBERS in
   * paths.ts and read back as numbers, like `budget`). This is the decision tier
   * a status pack needs — penny-accurate actuals stay in the finance system.
   */
  capex?: number;
  capexNote?: string;
  opex?: number;
  opexNote?: string;
  actualSpend?: number;
  forecast?: number;
  /**
   * THE RETENTION RATE — the percentage of each settled payment held back until
   * the supplier's obligations are complete (invoice-acceptance governance). A
   * headline figure only in v1: "retention held" is computed as this rate of the
   * paid lines, shown on the governance line. No per-line retention schedule.
   * A whole-number percent (e.g. 10 for 10%); absent, null or 0 all mean none is
   * held (0 is an explicit "no retention", null clears the field). Stored as its
   * own `retention_rate` column on the board.
   */
  retentionRate?: number | null;
  /*
   * THE BUDGET LEDGER — one line per invoice or commitment (migration 0116).
   *
   * Internal departmental budget tracking that replaces the single hand-typed
   * "actual spent". Each raised PO or received invoice is a line; the pool
   * totals are the SUM of the lines, so a figure is never overwritten. Stored
   * whole as jsonb (like `stakeholders`) — the money seam never looks inside
   * jsonb, so the line amounts stay plain numbers. Absent means no lines yet.
   */
  invoices?: InvoiceLine[];
  sponsor?: string;
  stakeholders?: Stakeholder[];
  risks?: string;
  successMeasure?: RichDoc | null;
  /**
   * THE SUPPLIER DELIVERING THIS PROJECT — migration 0100.
   *
   * The implementation partner or managed-service supplier a project is run
   * through. Distinct from a card's `external` (who-outside-the-team on one
   * card): this belongs to the whole project. Absent means in-house / unset.
   */
  vendor?: string;

  /*
   * THE CHARTER, EXPANDED — migration 0101 (PM Witness audit).
   *
   * The elements that make a definition a charter: what it will and will not
   * deliver, who runs it, what it assumes and is bound by, and the terms of a
   * vendor-delivered project. All optional, like the rest.
   *
   * Scope, assumptions and constraints are RICH TEXT (migration 0113) — a PM
   * writes them as bullet lists, and the requirements document renders them, so
   * they hold a RichDoc rather than a plain string. The short single-line fields
   * around them (the PM's name, the contract reference) stay plain text.
   */
  /** What the project will deliver — the in-scope statement, as rich text. */
  scopeIn?: RichDoc | null;
  /** What it explicitly will NOT deliver — read separately from scopeIn. Rich text. */
  scopeOut?: RichDoc | null;
  /** The PM accountable for delivery. NOT `owner` (a permission) — a role, free text. */
  projectManager?: string;
  /**
   * THE DESIGNATED APPROVER for this project's invoice/payment approvals — the one
   * person whose decision authorises a line for payment (invoice-approval
   * governance, 2 Oct). An email address. Absent means no one is designated, and
   * the fallback applies: only a workspace admin or a board manager/owner may
   * approve. A line may name its own approver (`InvoiceLine.approver`), which wins
   * over this. Stored on the board; the authority is enforced on the write path
   * (a Postgres trigger) and in `approvalAuthority`, not only in the drawer.
   */
  payApprover?: string;
  /** What the plan takes to be true — rich text. */
  assumptions?: RichDoc | null;
  /** The fixed limits the project must run inside — rich text. */
  constraints?: RichDoc | null;
  /** The contract / statement-of-work reference for a vendor-delivered project. */
  contractRef?: string;
  /** The named contact at the vendor. */
  vendorContact?: string;

  /*
   * ON HOLD — the project is parked on purpose (migration 0115).
   *
   * `heldAt` is when it was put on hold (ms); absent or null means it is
   * running, so its presence is the on/off and its age gives "on hold 14 days".
   * A parked project is not failing — it is waiting on capacity or a decision —
   * so it reads as its own state on the Plan and the portfolio, distinct from a
   * schedule or budget verdict. `holdReason` is why; `holdPickup` is when it
   * will be picked up, free text the way a card's `parked` blocker asks
   * ("Picked up when? e.g. after the launch").
   */
  heldAt?: number | null;
  holdReason?: string;
  holdPickup?: string;
}

/** How urgent a project is — the same four values a card uses, so it is one vocabulary. */
export type ProjectPriority = 'critical' | 'high' | 'medium' | 'low';

/** A stakeholder named in the charter step. A small list, stored as jsonb. */
export interface Stakeholder {
  name: string;
  role?: string;
}

/**
 * WHERE A LINE STANDS IN THE ACCEPTANCE GATE — invoice-acceptance governance.
 *
 * The single-step committed → paid of 0116 becomes a five-node gate so that the
 * money only moves after the work is verified and the payment is authorised:
 *
 *   committed  a PO is raised — obligated, no invoice yet
 *   received   the supplier's invoice is in (and, later, its PDF attached)
 *   accepted   a person has verified the work was actually delivered
 *   approved   a (second) person has authorised the payment
 *   paid       the invoice is settled — real money has moved
 *
 * The state only ever MOVES forward, and a line lives in exactly one of them, so
 * committed and paid are still never double-counted. Everything up to `paid`
 * counts as committed (raised, unpaid) in the rollup; only `paid` is actual
 * spend. Pre-gate data (lines that were only ever 'committed' or 'paid') reads
 * unchanged.
 */
export type InvoiceStatus = 'committed' | 'received' | 'accepted' | 'approved' | 'paid';

/**
 * FROZEN DELIVERY EVIDENCE — the phase's card counts at the instant a line was
 * accepted, snapshotted onto the line so it never re-counts if the board moves
 * afterwards. `done` is delivered + dropped (every resolved card); `delivered`
 * is cards that shipped, `dropped` is cards cut (won't-do). The phase name is
 * frozen too, so a later rename cannot rewrite what the acceptance recorded.
 */
export interface AcceptanceEvidence {
  /** The phase this evidence is for; empty string means the line had no phase (or
   *  the basis was an epic / standalone milestone). */
  phaseId: string;
  /** The phase's name at accept time — frozen against a later rename. */
  phaseName?: string;
  /** Units in the basis at accept time (cards, or 1 for a standalone milestone). */
  total: number;
  /** Resolved units (delivered + dropped) at accept time. */
  done: number;
  /** Units that shipped (card status done, or a reached milestone) at accept time. */
  delivered: number;
  /** Units cut / won't-do at accept time (always 0 for a milestone basis). */
  dropped: number;
  /** When the snapshot was taken (ms). */
  at: number;
  /** What the delivery was measured against — a phase, an epic, or a milestone
   *  (#483). Absent on snapshots taken before the basis picker, which were always
   *  phase-based. */
  basisKind?: 'phase' | 'epic' | 'milestone';
  /** The basis's display label at snapshot time (phase/epic/milestone name), frozen
   *  like phaseName. */
  basisLabel?: string;
  /** The unit the counts are in: 'cards' (a phase, an epic, or a phase-backed
   *  milestone) or 'milestone' (a standalone milestone, counted reached / not). */
  unit?: 'cards' | 'milestone';
}

/**
 * WHAT A LINE'S DELIVERY IS MEASURED AGAINST — a phase, an epic, or a milestone
 * (#483). Replaces the old phase-only source of the acceptance's delivery evidence.
 * Separate from the line's `phase` (spend classification) and `milestone` (the
 * billing claim). `id` is a phase id, an epic theme, or a milestone key
 * ('phase:<id>' | 'ms:<id>' | 'golive'). Stored in the `invoices` jsonb, no column.
 */
export interface DeliveryBasis {
  kind: 'phase' | 'epic' | 'milestone';
  id: string;
}

/** The supplier's invoice document, stored in the app's file bucket (0116 pattern). */
export interface InvoiceFileRef {
  /** The storage key the file lives at — passed to backend.fileUrl to read it. */
  path: string;
  /** The original filename, shown on the line. */
  name: string;
  /** Size in bytes, shown beside the name. */
  size: number;
  /** When it was attached (ms). */
  addedAt: number;
  /**
   * SHA-256 of the file's bytes, hex, computed when the file is attached. This is
   * what binds a pay approval to THIS exact document: the authority-to-pay page
   * cites it, and if the file is ever replaced the new hash will not match the one
   * the approval was taken against, so the stamp is void and the line must be
   * approved again. Absent on a line whose file predates hashing (treated as
   * unbindable — a fresh attach fixes it).
   */
  hash?: string;
}

/**
 * A LINE IN THE BUDGET LEDGER — one invoice or commitment (migration 0116),
 * carried through the acceptance gate (invoice-acceptance governance).
 *
 * Amounts are ex-tax positive numbers, in the project's currency. The list is
 * stored whole as jsonb on the project, so the gate fields below add no columns.
 */
export interface InvoiceLine {
  /** Stable id (crypto.randomUUID) so a line can be settled or removed. */
  id: string;
  /** Ex-tax amount — the invoice figure once received, else the PO figure. */
  amount: number;
  /** Which approved pool it draws from. */
  pool: 'capex' | 'opex';
  /** Where the line stands in the acceptance gate. Everything before 'paid' is
   *  committed (unpaid) in the rollup; only 'paid' is actual spend. */
  status: InvoiceStatus;
  /** The line's date (invoice date, or the PO date while committed), YYYY-MM-DD. */
  date?: string;
  /** Who the spend is with. Free text in v1; a workspace supplier list comes later. */
  vendor?: string;
  /** What it is for, one line. */
  description?: string;
  /** The invoice reference, once there is an invoice. */
  invoiceRef?: string;
  /** The purchase-order number, for a committed line matched later to its invoice. */
  poNumber?: string;
  /**
   * THE AMOUNT THE PO WAS RAISED AT — captured when the line is committed, so
   * the invoice figure (`amount`, edited once the invoice is in) can be checked
   * against it. When both are set and differ, the drawer shows the PO-vs-invoice
   * variance. Absent on a line entered straight as paid (no PO stage). Inside the
   * jsonb, so no column.
   */
  poAmount?: number;

  /*
   * THE ACCEPTANCE-GATE RECORDS — who did what, and when, on the way to paid.
   * Each is set as its step is cleared and never rewritten. All inside the
   * `invoices` jsonb, so the gate adds no columns to the board.
   */
  /** When the invoice was received (committed → received), ms. */
  receivedAt?: number;
  /** Who accepted the work — the actor's email, resolved to a name for display. */
  acceptedBy?: string;
  /** When it was accepted (received → accepted), ms. */
  acceptedAt?: number;
  /** The frozen delivery evidence captured at accept time — never re-counted. */
  acceptance?: AcceptanceEvidence;
  /** Who approved the payment — the actor's email. */
  approvedBy?: string;
  /** When it was approved (accepted → approved), ms. */
  approvedAt?: number;
  /** When it was paid (approved → paid), ms. Real money moved at this point. */
  paidAt?: number;
  /** The supplier's invoice document, attached to the line. */
  invoiceFile?: InvoiceFileRef;

  /*
   * THE APPROVAL-AUTHORITY RECORDS — who was entitled to approve, and how they
   * were (invoice-approval governance, 2 Oct). All inside the `invoices` jsonb, so
   * no columns. The authority rule itself lives in `approvalAuthority` and is
   * enforced on the write path by a trigger; these fields record what happened.
   */
  /** The line's own designated approver (email), overriding the project's. Absent
   *  → the project's `payApprover` applies. */
  approver?: string;
  /** When "send for approval" was pressed (accepted → awaiting the approver), ms.
   *  Set only once the invoice is attached (the upload gate). */
  approvalRequestedAt?: number;
  /** Who raised the approval request — the actor's email. */
  approvalRequestedBy?: string;
  /** HOW the approval was given: by the designated approver, by their active cover,
   *  or by an admin/manager standing in. Drives the stamp, the history and the
   *  finance queue. */
  approvalBasis?: ApprovalBasis;
  /** When the approval came from a cover, the approver they covered for (email), for
   *  the "approved as delegated cover for <name>" stamp. */
  approvalCoverFor?: string;
  /** The mandatory reason recorded when an admin approved in the designated
   *  approver's place, or overrode the separation-of-duties block. Its presence is
   *  what makes the approval a control exception. */
  approvalReason?: string;
  /** True when an admin approved a line they themselves accepted — the
   *  separation-of-duties override. Always carries an `approvalReason`. */
  sodOverride?: boolean;
  /**
   * THE INVOICE HASH THE APPROVAL WAS TAKEN AGAINST — a snapshot of
   * `invoiceFile.hash` at the moment the line was approved for payment. The
   * authority-to-pay page certifies this value, so the approval is cryptographically
   * bound to that exact document. If the file is later replaced, its new hash will
   * not match this one, which voids the stamp (see `payStampState`) and re-opens the
   * line for approval. Absent on a line approved before hashing existed. */
  approvalFileHash?: string;

  /**
   * CORRECTIONS to the line's classification — changing capex↔opex or the phase
   * after it was first set (line-correction governance). Each carries a mandatory
   * reason and is kept for the audit. Up to & including approved (not yet paid) a
   * correction is self-serve and `applied` at once; once the line is PAID a
   * correction is `pending` until an admin who did not raise it approves it. All
   * inside the `invoices` jsonb, so no column.
   */
  corrections?: Correction[];

  /*
   * THE LINE'S OWN CURRENCY — mixed currencies on one project (migration 0118).
   *
   * A project has ONE base (reporting) currency (the workspace's), but a payment
   * can be in another: some POs in TTD, some invoices in USD. `currency` is the
   * line's ISO code; absent means it is in the base. `fxRate` is the rate CAPTURED
   * on the line — base units per 1 foreign unit ("US$1 = TT$6.78" on a TTD-base
   * workspace → 6.78), so the base-equivalent is amount × fxRate. It is stored whenever a foreign line is
   * entered so history is never lost. A PAID line is frozen at its captured rate
   * (the money moved at that rate); a COMMITTED line follows the CURRENT workspace
   * rate, falling back to its captured rate. See lineBaseAmount.
   */
  currency?: string;
  fxRate?: number;

  /**
   * WHICH PHASE THE SPEND BELONGS TO — an optional phase id (the board's phases
   * table). Absent means the line is not tied to a phase (whole-project spend),
   * which the breakdown shows as "Unassigned". A spend breakdown only: assigning a
   * line here rolls it up under its phase so you can see where the money went by
   * phase, alongside the capex/opex split. No per-phase budget in v1. Stored in
   * the `invoices` jsonb, so no migration.
   */
  phase?: string;

  /**
   * WHAT THIS LINE'S DELIVERY IS MEASURED AGAINST — a phase, an epic, or a milestone
   * (#483). Replaces the old phase-only source of the acceptance's delivery evidence;
   * absent falls back to `phase` (every line entered before the basis picker). Kept
   * separate from `phase` (spend classification) and `milestone` (the billing claim).
   * Stored in the `invoices` jsonb, so no column.
   */
  deliveryBasis?: DeliveryBasis;

  /**
   * WHICH BILLABLE MILESTONE THIS LINE CLAIMS AGAINST — an optional milestone id
   * (the board's `milestones` table), mirroring `phase` exactly. Absent means the
   * line is not against a milestone (overheads, expenses), shown as "—". When set
   * to a billable milestone, the drawer shows a claim-of-value read (this line's
   * amount against the stage's bill value); the schedule of values rolls every
   * line's amount up under its milestone. The claim amount is the line's own
   * `amount` — part-claims and progress billing are natural. A DISPLAY/roll-up
   * link only: it never changes the spend rollup (spend reads invoice lines, a
   * milestone's value is a slice of the contract). Stored in the `invoices` jsonb,
   * so no migration.
   */
  milestone?: string;

  /**
   * WHEN THE PAYMENT IS DUE — the date finance must pay the supplier by, YYYY-MM-DD.
   * Captured on the line and surfaced on the finance "Authorised to pay" queue so
   * finance can prioritise what to pay first; absent means no due date was set.
   * Unlike the figures, it is NOT locked by approval — a due date is housekeeping
   * that often needs correcting after the fact. Stored in the `invoices` jsonb, so
   * no migration. (Invoice-approval governance, Deliverable 3.)
   */
  dueDate?: string;

  /**
   * THE DATE FINANCE ACTUALLY PAID — the value date from the finance/bank system,
   * YYYY-MM-DD, as opposed to `paidAt` (the immutable ms instant "Mark paid" was
   * clicked in FlightDeck). `paidAt` stays the event record; this is the business
   * paid date reconciled to Finance. Optional, and set or corrected by the
   * ledger-write tier (admin/manager) with a recorded reason — each change kept in
   * `paymentValueDateLog`. It can never be earlier than the approval; a future value
   * date is allowed with a warning. It does NOT touch the authority-to-pay stamp (a
   * separate fact by design). Stored in the `invoices` jsonb, so no column.
   * (#481, 10 Oct 2026.)
   */
  paymentValueDate?: string;
  /** The old→new / who / when / why trail of `paymentValueDate` edits, kept so a
   *  corrected finance date shows its history. Inside the `invoices` jsonb. (#481.) */
  paymentValueDateLog?: PaymentValueDateEdit[];

  /**
   * RECORD OF A PAYMENT APPROVED BELOW ITS DELIVERED SHARE — #483.
   *
   * When the board runs the delivery-% nudge (`deliveryNudge`) and an approver goes
   * ahead anyway, the fact is kept here: who approved, when, the delivered share at
   * that moment, and the bar it fell under. It is a NOTE, not a control exception —
   * the approver was entitled to approve; this records that the work was short of the
   * board's bar when they did. Absent on every line approved at or above the bar, and
   * on boards that do not run the nudge. Inside the `invoices` jsonb, so no column.
   */
  approvedBelowDelivery?: ApprovedBelowDelivery;

  /**
   * THE PAYMENT-APPROVAL CHAIN, FROZEN ONTO THE LINE — executive-approver tier
   * (4 Oct 2026). Absent until authorisation first opens; then the policy is
   * resolved to actual people and written here, ordered by stage, and never
   * rewritten — the audit must show who it ACTUALLY went to, not who would be asked
   * today (the same freeze the sign-off chain does onto `signoffs`). Each row is a
   * required signer; a row gains `signedAt`/`answer` as it is signed. The chain is
   * approved when every row is signed 'approved', rejected on the first 'no'. Stored
   * in the `invoices` jsonb, so no column. The legacy single-approval fields above
   * (approvedBy/approvedAt/approvalBasis…) stay for lines approved before the chain.
   */
  payChain?: PaymentChainRow[];
}

/**
 * ONE REQUIRED SIGNER on a line's frozen payment-approval chain — a resolved
 * person, which stage they answer, and (once they have) their signature. Shaped so
 * the sign-off chain's order and state helpers (`openStep`, `stateOf`, `howFar` in
 * signoff-policy.ts) read it directly: `step` is the stage id, `stageSeq` the frozen
 * dense rank, `signedAt`/`answer` the signature.
 */
export interface PaymentChainRow {
  /** The stage id this row answers (plays the role of a sign-off row's `step`). */
  step: string;
  /** The frozen dense stage rank (0,1,2…) — the order gate, like `signoffs.stage_seq`. */
  stageSeq: number;
  /** The stage's answer rule when not 'all' (so the chain can settle a stage on
   *  'any one' / a quorum). Absent means 'all'. */
  stageMode?: SignoffStageMode;
  /** The band threshold (base currency) that put this stage in the chain, for
   *  display ("above £50,000"); absent on an all-payments stage. */
  band?: number;
  /** The required signer, frozen at the moment the chain was raised. */
  who: Signatory;
  /** Set once this row has been answered. */
  signedAt?: number;
  answer?: 'approved' | 'rejected';
  /** A mandatory reason — a rejection, or an admin overriding separation of duties. */
  reason?: string;
  /** When signed as the approver's active cover, who they covered for (email). */
  coverFor?: string;
  /** True when this signature broke separation of duties (signer accepted the work)
   *  and was allowed only by an admin override — carries a `reason`. */
  sodOverride?: boolean;
  /** The invoice fingerprint this signature was taken against, so a later file swap
   *  voids it exactly as the single-approval `approvalFileHash` does. */
  fileHash?: string;
}

/**
 * A LINE'S VALUE IN THE PROJECT'S BASE CURRENCY. The one place the conversion
 * rule lives, so the rollup, the graph and the display never disagree.
 *
 *   · a line in the base currency is itself, no rate involved;
 *   · a PAID foreign line is frozen at the rate captured on it (`fxRate`) — real
 *     money moved at that rate, so a later rate change must not rewrite history;
 *   · a COMMITTED foreign line follows the CURRENT workspace rate, so changing the
 *     rate re-flows open commitments; it falls back to the captured rate when the
 *     workspace has none.
 *
 * A rate is base-per-foreign (amount × rate = base). A missing or non-positive
 * rate cannot convert, so the face amount is returned rather than inventing a
 * number — the surface flags such a line separately.
 */
export function lineBaseAmount(
  line: Pick<InvoiceLine, 'amount' | 'currency' | 'status' | 'fxRate'>,
  baseCode: string,
  rates?: Record<string, number> | null,
): number {
  const cur = line.currency || baseCode;
  if (cur === baseCode) return line.amount;
  const current = rates?.[cur];
  const rate = line.status === 'paid' ? line.fxRate ?? current : current ?? line.fxRate;
  if (!rate || !Number.isFinite(rate) || rate <= 0) return line.amount;
  return line.amount * rate;
}

/** One pool's money, summed from the ledger against its approved plan. */
export interface PoolRollup {
  /** The approved plan for this pool (project.capex / project.opex). */
  planned?: number;
  /** Sum of committed (raised, unpaid) lines. */
  committed: number;
  /** Sum of paid lines — the real spend for this pool. */
  actual: number;
  /** planned − committed − actual, only when a plan is set (may go negative). */
  remaining?: number;
}

/** The whole budget, rolled up from the ledger. Committed and actual (paid) are
 *  kept apart and never merged, so "spent" is only what has actually been paid.
 *  The TOTAL-level figures anchor the ledger to the approved budget (the quote):
 *  every serious cost tool leads with Remaining = Budget − Committed − Actual and
 *  shows the pools as a breakdown beneath it. */
export interface BudgetRollup {
  capex: PoolRollup;
  opex: PoolRollup;
  committedTotal: number;
  actualTotal: number;
  /** The approved baseline, echoed for the total band; absent when no budget is set. */
  budget?: number;
  /** budget − committed − actual: what is still available against the approved budget. */
  remaining?: number;
  /** budget − capex − opex: the baseline not yet split into a pool (may be 0 or negative). */
  unclassified?: number;
  /** How many ledger lines are in a currency other than the base — drives the
   *  "totals converted from other currencies" caveat. Absent when all are base. */
  foreignCount?: number;
}

/** The base currency and current rates a rollup converts foreign lines with. When
 *  omitted, lines are summed at face value — correct for a single-currency ledger
 *  (every line in the base), which is the common case and every pre-0118 project. */
export interface BudgetContext {
  /** The project's base (reporting) currency — the workspace's. */
  base?: string;
  /** Current base-per-foreign rates by code, for converting COMMITTED lines live. */
  rates?: Record<string, number> | null;
}

/** Sum a project's ledger into per-pool committed/actual totals AND against the
 *  approved budget total, every figure in the BASE currency. Pure. The budget
 *  baseline is authored in the base, so it needs no conversion; each line is
 *  converted by lineBaseAmount (paid frozen, committed at the current rate). */
export function rollupBudget(
  project: Pick<Project, 'budget' | 'capex' | 'opex' | 'invoices'>,
  ctx?: BudgetContext,
): BudgetRollup {
  const lines = project.invoices ?? [];
  const base = ctx?.base ?? '';
  let foreignCount = 0;
  for (const l of lines) if ((l.currency || base) !== base) foreignCount += 1;
  const pool = (which: 'capex' | 'opex', planned?: number): PoolRollup => {
    let committed = 0;
    let actual = 0;
    for (const l of lines) {
      if (l.pool !== which) continue;
      const amt = Number.isFinite(l.amount) ? lineBaseAmount(l, base, ctx?.rates) : 0;
      if (l.status === 'paid') actual += amt;
      else committed += amt;
    }
    const r: PoolRollup = { planned, committed, actual };
    if (planned != null) r.remaining = planned - committed - actual;
    return r;
  };
  const capex = pool('capex', project.capex);
  const opex = pool('opex', project.opex);
  const committedTotal = capex.committed + opex.committed;
  const actualTotal = capex.actual + opex.actual;
  const roll: BudgetRollup = { capex, opex, committedTotal, actualTotal };
  if (foreignCount > 0) roll.foreignCount = foreignCount;
  // The total band: the ledger tied to the approved budget. Remaining is what is
  // still available; unclassified is the baseline not yet split into a pool.
  if (project.budget != null) {
    roll.budget = project.budget;
    roll.remaining = project.budget - committedTotal - actualTotal;
    roll.unclassified = project.budget - (project.capex ?? 0) - (project.opex ?? 0);
  }
  return roll;
}

/** One phase's committed/actual spend, in the base currency. */
export interface PhaseSpend {
  committed: number;
  actual: number;
}

/**
 * Sum a project's ledger BY PHASE, every figure in the BASE currency (the same
 * conversion rule as the pool rollup). Keyed by phase id; a line with no phase
 * falls under the empty-string key — the caller shows that as "Unassigned". Pure:
 * the caller orders and names the phases from the board's phase list, since only
 * it knows their order and current names. A spend breakdown only — no per-phase
 * budget, so no remaining/variance here.
 */
export function phaseBreakdown(
  project: Pick<Project, 'invoices'>,
  ctx?: BudgetContext,
): Record<string, PhaseSpend> {
  const lines = project.invoices ?? [];
  const base = ctx?.base ?? '';
  const out: Record<string, PhaseSpend> = {};
  for (const l of lines) {
    const key = l.phase || '';
    const amt = Number.isFinite(l.amount) ? lineBaseAmount(l, base, ctx?.rates) : 0;
    const e = (out[key] ??= { committed: 0, actual: 0 });
    if (l.status === 'paid') e.actual += amt;
    else e.committed += amt;
  }
  return out;
}

/* ══════════════════════════════════════════════════════════════════════════════
   THE ACCEPTANCE GATE — invoice-acceptance governance (pure helpers).

   Everything here is derived from the ledger and the board so it can be tested
   without a database. The store actions enforce the same rules on the write path.
   ══════════════════════════════════════════════════════════════════════════════ */

/** The gate order — the single path a line moves along, never backwards. */
export const INVOICE_STAGES: InvoiceStatus[] =
  ['committed', 'received', 'accepted', 'approved', 'paid'];

/** The single word each status reads as, sentence case (never uppercased). */
export const INVOICE_STATUS_WORD: Record<InvoiceStatus, string> = {
  committed: 'Committed', received: 'Received', accepted: 'Accepted', approved: 'Approved', paid: 'Paid',
};
/** The status pill's colour tone: committed is neutral, the in-flight words are
 *  amber (in-progress), paid is green. */
export const INVOICE_STATUS_TONE: Record<InvoiceStatus, 'committed' | 'flow' | 'paid'> = {
  committed: 'committed', received: 'flow', accepted: 'flow', approved: 'flow', paid: 'paid',
};

/** How far a line has cleared: the index of its status in the gate (0–4). */
export function stageIndex(status: InvoiceStatus): number {
  const i = INVOICE_STAGES.indexOf(status);
  return i < 0 ? 0 : i;
}

/**
 * THE PAID GATE — a line may only be marked paid once it has been BOTH accepted
 * AND approved. Enforced here (and in the store) so real money never moves on
 * work nobody verified or authorised. Reads the records, not the status, so a
 * line dragged straight to 'approved' with no accept stamp still cannot pay.
 */
export function canMarkPaid(line: Pick<InvoiceLine, 'acceptedAt' | 'approvedAt' | 'status'>): boolean {
  if (line.status === 'paid') return false; // already settled — nothing to unlock
  return line.acceptedAt != null && line.approvedAt != null;
}

/** A line's identity for duplicate detection: reference + vendor + amount, each
 *  normalised. A blank reference cannot key a duplicate — too many real lines
 *  share a vendor and a round amount — so it returns null and never matches. */
function duplicateKey(line: Pick<InvoiceLine, 'invoiceRef' | 'vendor' | 'amount'>): string | null {
  const ref = (line.invoiceRef ?? '').trim().toLowerCase();
  if (!ref) return null;
  const vendor = (line.vendor ?? '').trim().toLowerCase();
  return `${ref} ${vendor} ${line.amount}`;
}

/**
 * DERIVED DUPLICATE DETECTION — the ids of every line whose (invoiceRef + vendor
 * + amount) matches at least one OTHER line on the board. Both sides of a match
 * are flagged, so either row shows the warning. A line with no reference is never
 * flagged.
 */
export function duplicateLineIds(lines: InvoiceLine[]): Set<string> {
  const byKey = new Map<string, string[]>();
  for (const l of lines) {
    const k = duplicateKey(l);
    if (!k) continue;
    const ids = byKey.get(k) ?? [];
    ids.push(l.id);
    byKey.set(k, ids);
  }
  const flagged = new Set<string>();
  for (const ids of byKey.values()) {
    if (ids.length > 1) for (const id of ids) flagged.add(id);
  }
  return flagged;
}

/**
 * SEGREGATION-OF-DUTIES — true when the same person both accepted and approved a
 * line. Since 2 Oct this is a HARD BLOCK on the write path (the accepter cannot
 * approve), so it can only ever be true when an admin deliberately overrode it —
 * which also sets `sodOverride` and a reason. Kept as the record of the fact, and
 * still a control exception. Compared on the actor's address.
 */
export function sameHandLine(line: Pick<InvoiceLine, 'acceptedBy' | 'approvedBy'>): boolean {
  const a = (line.acceptedBy ?? '').trim().toLowerCase();
  const b = (line.approvedBy ?? '').trim().toLowerCase();
  return !!a && !!b && a === b;
}

/** How an approval's authority was exercised — by the designated approver, by their
 *  active sign-off cover, or by an admin/manager standing in. */
export type ApprovalBasis = 'approver' | 'cover' | 'admin';

/**
 * THE VERDICT on whether one person may approve one line for payment, and on what
 * basis. The SINGLE rule the drawer (to show the control's state), the store action
 * (to enforce on the write path) and the backend trigger (in spirit) all read.
 * Pure — everything it needs comes in as arguments, so it tests without a database.
 */
export interface ApprovalAuthority {
  /** Whether this person may approve this line at all. */
  allowed: boolean;
  /** How, when allowed. */
  basis?: ApprovalBasis;
  /** When `basis === 'cover'`, the designated approver being covered for (email). */
  coverFor?: string;
  /** A mandatory reason must accompany the approval — an admin standing in for an
   *  absent approver, or an admin overriding separation of duties. */
  needsReason: boolean;
  /** True when the approval breaks separation of duties (the approver also accepted
   *  the line) and is allowed only because an admin is overriding it. */
  sodOverride: boolean;
  /** When NOT allowed, a plain sentence for the disabled control. */
  blockedWhy?: string;
}

/**
 * WHO MAY APPROVE A PAYMENT — invoice-approval governance, 2 Oct. A line may be
 * approved only by its DESIGNATED APPROVER (the line's own, else the project's
 * `payApprover`), that approver's ACTIVE SIGN-OFF COVER, or an ADMIN/manager
 * standing in. Separation of duties is a hard block: whoever ACCEPTED the work may
 * not approve its payment — only an admin may override that, and only with a reason.
 * The admin fallback requires a reason too when it stands in for a designated
 * approver, so it never quietly becomes the everyday path.
 */
export function approvalAuthority(opts: {
  line: Pick<InvoiceLine, 'acceptedBy' | 'approver'>;
  /** The project's designated approver (`Project.payApprover`). */
  projectApprover?: string;
  /** The person trying to approve — their email. */
  me: string;
  /** A workspace admin, or a manager/owner of this board — the fallback tier. */
  isAdmin: boolean;
  /** Is there an active sign-off cover from `approver` to me right now? */
  hasActiveCoverFrom: (approver: string) => boolean;
}): ApprovalAuthority {
  const me = (opts.me ?? '').trim().toLowerCase();
  const designated = ((opts.line.approver ?? opts.projectApprover) ?? '').trim().toLowerCase();
  const accepter = (opts.line.acceptedBy ?? '').trim().toLowerCase();
  const iAccepted = !!me && me === accepter;

  if (!me) return { allowed: false, needsReason: false, sodOverride: false, blockedWhy: 'Sign in to approve.' };

  const amApprover = !!designated && me === designated;
  const amCover = !!designated && opts.hasActiveCoverFrom(designated);

  // Separation of duties — the accepter of the work may not approve its payment.
  if (iAccepted) {
    if (opts.isAdmin) {
      return {
        allowed: true,
        basis: amApprover ? 'approver' : amCover ? 'cover' : 'admin',
        coverFor: amCover ? designated : undefined,
        needsReason: true,
        sodOverride: true,
      };
    }
    return {
      allowed: false,
      needsReason: false,
      sodOverride: false,
      blockedWhy: 'You verified this work, so you cannot also approve its payment.',
    };
  }

  if (amApprover) return { allowed: true, basis: 'approver', needsReason: false, sodOverride: false };
  if (amCover) return { allowed: true, basis: 'cover', coverFor: designated, needsReason: false, sodOverride: false };
  if (opts.isAdmin) {
    // Admin/manager standing in. A reason is required only when standing in for a
    // designated approver (G8 — stop the fallback becoming the normal path). With
    // nobody designated, admin approval is the configured path and needs none.
    return { allowed: true, basis: 'admin', needsReason: !!designated, sodOverride: false };
  }
  return {
    allowed: false,
    needsReason: false,
    sodOverride: false,
    blockedWhy: 'Only the designated approver, their cover, or an admin may approve this.',
  };
}

/** Every line carrying a control exception — a duplicate, an admin override of the
 *  separation-of-duties block, or an admin standing in for the approver (a reason
 *  recorded). Drives the row's exception dot and the "N exceptions" pill. */
export function controlExceptionIds(lines: InvoiceLine[]): Set<string> {
  const dups = duplicateLineIds(lines);
  const out = new Set<string>(dups);
  for (const l of lines) {
    const adminStoodIn = l.approvalBasis === 'admin' && !!(l.approvalReason ?? '').trim();
    if (sameHandLine(l) || l.sodOverride || adminStoodIn) out.add(l.id);
  }
  return out;
}

/**
 * THE AUTHORITY STAMP ON AN APPROVED LINE — invoice-approval governance, 2 Oct
 * (Deliverable 2). Once a line is approved for payment, its recorded authority
 * facts (who, on what basis, cover-for, the reason) become a tamper-evident stamp:
 * it is drawn onto the "Authority to pay" page bound to the invoice, shown on the
 * line, and read by the finance "Authorised to pay" queue. These helpers compose
 * what the stamp SAYS from those facts — pure, so the panel, the PDF and the queue
 * all read one source and a test needs no database.
 *
 * NO NEW STORED FIELD. The reference is DERIVED from the line id and the approval
 * moment, so it is stable while the approval stands and changes the instant the
 * line is re-approved — which is the point: re-opening approval (a re-upload, an
 * edit) voids the old stamp, and a new approval mints a new reference.
 */
export function payStampRef(line: Pick<InvoiceLine, 'id' | 'approvedAt'>): string {
  if (!line.approvedAt) return '';
  // A small stable hash of id+moment → a 4-digit human reference. FNV-1a, enough
  // to read aloud and match a row to its stamp; not a secret and not an id.
  const seed = `${line.id}:${line.approvedAt}`;
  let h = 0x811c9dc5;
  for (let i = 0; i < seed.length; i++) {
    h ^= seed.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  const n = (h >>> 0) % 10000;
  return `SA-${String(n).padStart(4, '0')}`;
}

/** The three stamp readings the approved mock draws, chosen by how the approval was
 *  given. A control exception (an admin standing in, or a separation-of-duties
 *  override — both carry a reason) is called out as an exception; a designated
 *  approver or their delegated cover is not. */
export type PayStampKind = 'approver' | 'cover' | 'exception';

export function payStampKind(
  line: Pick<InvoiceLine, 'approvalBasis' | 'approvalReason' | 'sodOverride'>,
): PayStampKind {
  const hasReason = !!(line.approvalReason ?? '').trim();
  if (line.sodOverride || (line.approvalBasis === 'admin' && hasReason)) return 'exception';
  if (line.approvalBasis === 'cover') return 'cover';
  return 'approver';
}

/**
 * WHETHER THE STAMP STILL BINDS — the tamper check. A pay approval is taken against
 * a specific invoice document, and `approvalFileHash` records that document's hash.
 * The stamp is:
 *   · 'none'   — the line is not approved, so there is no stamp;
 *   · 'valid'  — approved AND the invoice on the line still hashes to what was
 *                approved, so the authority holds;
 *   · 'voided' — approved but the invoice has changed (or cannot be matched), so the
 *                authority no longer binds and the line must be approved again.
 * A line approved before hashing existed (no `approvalFileHash`) reads 'valid' so old
 * approvals are not falsely flagged; a fresh approval always carries the hash.
 */
export type PayStampState = 'none' | 'valid' | 'voided';

export function payStampState(
  line: Pick<InvoiceLine, 'approvedAt' | 'approvalFileHash' | 'invoiceFile'>,
): PayStampState {
  if (!line.approvedAt) return 'none';
  const taken = (line.approvalFileHash ?? '').trim();
  if (!taken) return 'valid'; // approved before hashing — nothing to check against
  const now = (line.invoiceFile?.hash ?? '').trim();
  return now && now === taken ? 'valid' : 'voided';
}

/**
 * THE FINANCE "AUTHORISED TO PAY" QUEUE — Deliverable 3. Everything that has been
 * approved for payment but not yet paid, across every board finance can see, in one
 * read-only list: the invoices finance pays and the proof of who authorised each.
 *
 * A line is IN the queue exactly while its status is 'approved' — approved for
 * payment (so finance may pay it) but not yet 'paid' (so it still needs paying).
 * Pure, taking the boards it is handed, so it needs no store to test and the finance
 * screen, the export and a test all read one definition.
 */
export interface AuthorisedLine {
  boardId: string;
  boardName: string;
  line: InvoiceLine;
}

export function authorisedToPay(
  boards: { id: string; name: string; invoices?: InvoiceLine[] }[],
): { items: AuthorisedLine[]; exceptionCount: number } {
  const items: AuthorisedLine[] = [];
  for (const b of boards) {
    for (const line of b.invoices ?? []) {
      if (line.status === 'approved') items.push({ boardId: b.id, boardName: b.name, line });
    }
  }
  // Newest authority first — the most recently approved sits at the top, the way a
  // to-do queue reads.
  items.sort((a, z) => (z.line.approvedAt ?? 0) - (a.line.approvedAt ?? 0));
  const exceptionCount = items.filter((it) => payStampKind(it.line) === 'exception').length;
  return { items, exceptionCount };
}

/**
 * ONE ROW OF THE FINANCE EXPORT — a flat record per authorised invoice, with the
 * authority resolved to names. The component assembles these (it has the people and
 * the currency); the CSV is built from them here so the format is tested in one
 * place. Deliberately NEUTRAL — plain columns any finance system can import, never a
 * FlightDeck-shaped blob (pm-witness, 2 Oct).
 */
export interface FinanceExportRow {
  board: string;
  vendor: string;
  amount: number;
  currency: string;
  invoiceRef: string;
  poNumber: string;
  dueDate: string;
  classification: string;
  stampRef: string;
  authorisedBy: string;
  basis: string;
  coverFor: string;
  controlException: string;
  invoiceFingerprint: string;
}

/** The export as CSV text (RFC-4180 quoting). A header row then one row per invoice. */
export function authorisedToPayCsv(rows: FinanceExportRow[]): string {
  const headers = [
    'Board', 'Vendor', 'Amount', 'Currency', 'Invoice ref', 'PO number', 'Due date',
    'Classification', 'Authority ref', 'Authorised by', 'Basis', 'Cover for',
    'Control exception', 'Invoice fingerprint',
  ];
  const cell = (v: string | number): string => {
    const s = String(v ?? '');
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const lines = [headers.map(cell).join(',')];
  for (const r of rows) {
    lines.push([
      r.board, r.vendor, r.amount, r.currency, r.invoiceRef, r.poNumber, r.dueDate,
      r.classification, r.stampRef, r.authorisedBy, r.basis, r.coverFor,
      r.controlException, r.invoiceFingerprint,
    ].map(cell).join(','));
  }
  return lines.join('\n');
}

/**
 * THE PAYMENTS TAB ON SIGN-OFFS — Deliverable A. One place for "what needs YOUR
 * decision" on the money: the budget-line pay-approvals and the paid-line
 * corrections routed to the signed-in person, each row saying WHY it is theirs.
 *
 * Two sources, both on the board's own lines:
 *   · a PAY-APPROVAL — an accepted line, invoice attached, that this person is
 *     entitled to approve (its designated approver, their active cover, or an admin
 *     standing in). It is answered on the line (the drawer), where the approver sees
 *     the document first; this row is the route to it.
 *   · a CORRECTION on a PAID line — a reclassification that needs an admin who did
 *     NOT raise it. Pending ones need a decision; settled ones (approved/rejected)
 *     are kept for the record, as the other tabs keep theirs.
 *
 * Pure: everything it needs is passed in (the authority predicate is shared with the
 * drawer and the store), so the tab, the count and a test all read one definition.
 * Names and amounts are resolved by the screen, which has the people and the money.
 */
export type PaymentReviewKind = 'pay' | 'correction';
export type PaymentReviewState = 'waiting' | 'approved' | 'rejected';
/** Why this decision is yours — drives the row's "why it's yours" line and its tone. */
export type PaymentReviewWhy = 'approver' | 'cover' | 'admin';

export interface PaymentReview {
  /** Unique per row (the line, plus the correction id for a correction row). */
  rowKey: string;
  lineId: string;
  kind: PaymentReviewKind;
  vendor: string;
  /** The line's face amount and currency (for a pay row's title). */
  amount: number;
  currency?: string;
  /** A correction row's change — the fields are a pool word, a phase id or a
   *  milestone id. */
  change?: { field: 'pool' | 'phase' | 'milestone'; from: string; to: string };
  /** Who raised it — an email the screen resolves to a name. */
  requestedBy: string;
  askedAt?: number;
  state: PaymentReviewState;
  /** Waiting AND routed to me — what the teal dot and the "need you" count mean. */
  needsMe: boolean;
  why: PaymentReviewWhy;
  /** When `why === 'cover'`, the approver being covered for (email). */
  coverFor?: string;
  /** A control exception — an admin standing in, or overriding separation of duties. */
  isException: boolean;
  /** Settled rows carry who decided, when, and (on a reject) why. */
  settledBy?: string;
  settledAt?: number;
  settledReason?: string;
}

export function paymentsForApprover(
  board: { invoices?: InvoiceLine[] },
  ctx: {
    me: string;
    isAdmin: boolean;
    hasActiveCoverFrom: (approver: string) => boolean;
    projectApprover?: string;
  },
): PaymentReview[] {
  const me = (ctx.me ?? '').trim().toLowerCase();
  const out: PaymentReview[] = [];
  for (const line of board.invoices ?? []) {
    // ── A pay-approval: accepted, invoice attached, and I may authorise it. ──
    if (line.status === 'accepted' && line.invoiceFile) {
      const auth = approvalAuthority({
        line,
        projectApprover: ctx.projectApprover,
        me: ctx.me,
        isAdmin: ctx.isAdmin,
        hasActiveCoverFrom: ctx.hasActiveCoverFrom,
      });
      if (auth.allowed) {
        const why: PaymentReviewWhy = auth.basis === 'cover' ? 'cover' : auth.basis === 'admin' ? 'admin' : 'approver';
        out.push({
          rowKey: `pay:${line.id}`,
          lineId: line.id,
          kind: 'pay',
          vendor: line.vendor ?? '',
          amount: line.amount,
          currency: line.currency,
          requestedBy: line.approvalRequestedBy ?? line.acceptedBy ?? '',
          askedAt: line.approvalRequestedAt ?? line.acceptedAt,
          state: 'waiting',
          needsMe: true,
          why,
          coverFor: auth.coverFor,
          isException: auth.sodOverride || auth.needsReason,
        });
      }
    }
    // ── Corrections on paid lines: pending (need an admin ≠ requester) + settled. ──
    for (const c of line.corrections ?? []) {
      if (c.status === 'applied') continue; // self-serve, never went for approval
      const settled = c.status === 'approved' || c.status === 'rejected';
      const iRaised = !!me && (c.by ?? '').trim().toLowerCase() === me;
      const needsMe = c.status === 'pending' && ctx.isAdmin && !iRaised;
      out.push({
        rowKey: `cor:${line.id}:${c.id}`,
        lineId: line.id,
        kind: 'correction',
        vendor: line.vendor ?? '',
        amount: line.amount,
        currency: line.currency,
        change: { field: c.field, from: c.from, to: c.to },
        requestedBy: c.by,
        askedAt: c.at,
        state: c.status === 'approved' ? 'approved' : c.status === 'rejected' ? 'rejected' : 'waiting',
        needsMe,
        // A correction is an admin's call; the exception tag rides the settled reason.
        why: 'admin',
        isException: settled ? !!(c.decideReason ?? '').trim() && c.status === 'rejected' : false,
        settledBy: settled ? c.decidedBy : undefined,
        settledAt: settled ? c.decidedAt : undefined,
        settledReason: settled ? c.decideReason : undefined,
      });
    }
  }
  // Newest first within a state; the screen splits waiting from settled.
  out.sort((a, z) => (z.askedAt ?? 0) - (a.askedAt ?? 0));
  return out;
}

/**
 * A CORRECTION to a line's classification — changing capex↔opex or the phase after
 * it was first set (line-correction governance, 2 Oct). Kept on the line for the
 * audit. A reason is always required.
 */
export interface Correction {
  id: string;
  /** When it was raised, ms. */
  at: number;
  /** Who raised it — the actor's email. */
  by: string;
  /** What it changes. */
  field: 'pool' | 'phase' | 'milestone';
  /** The value before — a pool word, a phase id, or a milestone id ('' for none). */
  from: string;
  /** The value after. */
  to: string;
  /** Why — always required, at every tier. */
  reason: string;
  /** 'applied' at once (self-serve), 'pending' while a paid-line correction waits
   *  for approval, then 'approved' or 'rejected'. */
  status: 'applied' | 'pending' | 'approved' | 'rejected';
  /** Who approved or rejected a paid-line correction — the actor's email. */
  decidedBy?: string;
  /** When it was approved or rejected, ms. */
  decidedAt?: number;
  /** Why a paid-line correction was rejected — always set on a reject. */
  decideReason?: string;
}

/**
 * THE ONE TIER RULE. A PAID line's correction needs an admin's approval (the money
 * has moved, so reclassifying it is the highest-scrutiny change); up to & including
 * approved-but-not-paid, a correction is self-serve and applies the moment it is
 * made. Enforced in the store write path, not only in the dialog.
 */
export function correctionNeedsApproval(line: Pick<InvoiceLine, 'status'>): boolean {
  return line.status === 'paid';
}

/** The line's correction still waiting for a decision, if any. */
export function pendingCorrection(line: Pick<InvoiceLine, 'corrections'>): Correction | undefined {
  return (line.corrections ?? []).find((c) => c.status === 'pending');
}

/**
 * ONE EDIT TO THE FINANCE PAYMENT DATE — the old→new / who / when / why record kept
 * when `paymentValueDate` is set or corrected, so a reconciled finance date shows its
 * history (the standard correction record, #481). Inside the `invoices` jsonb.
 */
export interface PaymentValueDateEdit {
  /** When the edit was made, ms. */
  at: number;
  /** Who made it — the actor's email. */
  by: string;
  /** The value before (YYYY-MM-DD); absent the first time the date is set. */
  from?: string;
  /** The value after (YYYY-MM-DD). */
  to: string;
  /** Why — required when correcting an already-set date, else ''. */
  reason: string;
}

/** The civil day (YYYY-MM-DD, in local time) of a ms timestamp. */
export function civilDay(ms: number): string {
  const d = new Date(ms);
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

export type PaymentValueDateIssue = 'before-approval' | 'future' | null;

/**
 * WHAT (IF ANYTHING) IS WRONG WITH A PROPOSED FINANCE PAYMENT DATE (`value`, YYYY-MM-DD).
 * 'before-approval' is a HARD block — a payment cannot have cleared before it was
 * authorised. 'future' is a soft WARNING — a value date after today is unusual (the
 * money has not cleared yet) but allowed, since finance may enter a scheduled value
 * date. `todayISO` (YYYY-MM-DD, the caller's local today) is passed in so this stays
 * pure and testable.
 */
export function paymentValueDateIssue(
  line: Pick<InvoiceLine, 'approvedAt'>,
  value: string,
  todayISO: string,
): PaymentValueDateIssue {
  const v = (value ?? '').trim();
  if (!v) return null;
  if (line.approvedAt != null && v < civilDay(line.approvedAt)) return 'before-approval';
  if (v > todayISO) return 'future';
  return null;
}

/**
 * BUILD THE FROZEN DELIVERY EVIDENCE for a phase, from the board's cards as they
 * stand right now. The caller stores the result on the line at accept time; it is
 * never recomputed afterwards, so the record cannot drift when the board moves.
 * `done` counts every resolved card (delivered + dropped).
 */
export function buildPhaseEvidence(
  phaseId: string | undefined,
  phaseName: string | undefined,
  cards: ReadonlyArray<{ phase?: string | null; status: IssueStatus }>,
  at: number,
): AcceptanceEvidence {
  const pid = phaseId ?? '';
  const inPhase = cards.filter((c) => (c.phase ?? '') === pid);
  const delivered = inPhase.filter((c) => c.status === 'done').length;
  const dropped = inPhase.filter((c) => c.status === 'wontdo').length;
  return {
    phaseId: pid,
    ...(phaseName ? { phaseName } : {}),
    total: inPhase.length,
    done: delivered + dropped,
    delivered,
    dropped,
    at,
  };
}

/** The minimal milestone shape buildDeliveryEvidence reads — decoupled from the full
 *  Milestone type so projects.ts need not import it. */
export interface DeliveryMilestone {
  /** 'golive' | 'phase:<id>' | 'ms:<id>'. */
  key: string;
  name: string;
  /** Set once the milestone has landed. */
  actualDay?: string;
  /** 'done' once it is reached. */
  state?: string;
}

/**
 * THE EFFECTIVE DELIVERY BASIS FOR A LINE — its explicit `deliveryBasis`, else its
 * `phase` (backward compatibility — every line entered before the basis picker), else
 * none.
 */
export function lineDeliveryBasis(
  line: Pick<InvoiceLine, 'deliveryBasis' | 'phase'>,
): DeliveryBasis | undefined {
  if (line.deliveryBasis) return line.deliveryBasis;
  if (line.phase) return { kind: 'phase', id: line.phase };
  return undefined;
}

/**
 * BUILD THE DELIVERY EVIDENCE for a basis — the generalisation of buildPhaseEvidence
 * to a phase, an epic, or a milestone (#483). The caller freezes the result onto the
 * line at accept time; it never recomputes, so the record cannot drift.
 *   - phase: cards whose phase is the id.
 *   - epic: cards whose epic theme is the id.
 *   - milestone, phase-backed ('phase:<pid>'): that phase's cards.
 *   - milestone, standalone ('ms:<id>' | 'golive'): the milestone's OWN reached state
 *     — one unit, delivered once it has landed (actualDay set or state 'done'); never
 *     a misleading "0 of 0" card count.
 * Returns null when the basis is absent or cannot be resolved.
 */
export function buildDeliveryEvidence(
  basis: DeliveryBasis | undefined,
  ctx: {
    cards: ReadonlyArray<{ phase?: string | null; epic?: string; status: IssueStatus }>;
    phaseName?: (id: string) => string | undefined;
    milestones?: ReadonlyArray<DeliveryMilestone>;
  },
  at: number,
): AcceptanceEvidence | null {
  if (!basis || !basis.id) return null;
  const cardEvidence = (
    cards: ReadonlyArray<{ status: IssueStatus }>,
    opts: { phaseId: string; phaseName?: string; basisLabel: string },
  ): AcceptanceEvidence => {
    const delivered = cards.filter((c) => c.status === 'done').length;
    const dropped = cards.filter((c) => c.status === 'wontdo').length;
    return {
      phaseId: opts.phaseId,
      ...(opts.phaseName ? { phaseName: opts.phaseName } : {}),
      basisKind: basis.kind,
      basisLabel: opts.basisLabel,
      unit: 'cards',
      total: cards.length,
      done: delivered + dropped,
      delivered,
      dropped,
      at,
    };
  };
  if (basis.kind === 'phase') {
    const name = ctx.phaseName?.(basis.id);
    return cardEvidence(
      ctx.cards.filter((c) => (c.phase ?? '') === basis.id),
      { phaseId: basis.id, phaseName: name, basisLabel: name ?? 'this phase' },
    );
  }
  if (basis.kind === 'epic') {
    return cardEvidence(
      ctx.cards.filter((c) => (c.epic ?? '').trim() === basis.id),
      { phaseId: '', basisLabel: basis.id },
    );
  }
  // milestone
  const ms = (ctx.milestones ?? []).find((m) => m.key === basis.id);
  const label = ms?.name ?? basis.id;
  if (basis.id.startsWith('phase:')) {
    const pid = basis.id.slice('phase:'.length);
    return cardEvidence(
      ctx.cards.filter((c) => (c.phase ?? '') === pid),
      { phaseId: pid, phaseName: ms?.name, basisLabel: label },
    );
  }
  // standalone / go-live — reached state, one unit (never a hollow card count)
  const reached = !!ms && (!!ms.actualDay || ms.state === 'done');
  return {
    phaseId: '',
    basisKind: 'milestone',
    basisLabel: label,
    unit: 'milestone',
    total: 1,
    done: reached ? 1 : 0,
    delivered: reached ? 1 : 0,
    dropped: 0,
    at,
  };
}

/**
 * THE DELIVERED SHARE of a piece of delivery evidence — delivered units over total,
 * in [0, 1]. Evidence with nothing to deliver (total 0) reads as fully delivered, so
 * it never trips the sub-X% nudge (#483). Dropped (won't-do) units count toward total
 * but not delivered, so cutting scope lowers the share honestly.
 */
export function deliveredShare(ev: Pick<AcceptanceEvidence, 'total' | 'delivered'>): number {
  return ev.total > 0 ? ev.delivered / ev.total : 1;
}

/**
 * THE NOTE kept on a line approved below the board's delivery bar (#483). Records
 * who approved, when, the delivered share at that moment (0–1), the bar it fell
 * under (pct, 0–100), and the reason the approver gave for approving ahead of
 * delivery — required (#483 option b). A record of a fact plus the stated why.
 */
export interface ApprovedBelowDelivery {
  at: number;
  by: string;
  /** The delivered share at approval, 0–1 (what `deliveredShare` returned). */
  share: number;
  /** The board's bar at approval, a percentage 0–100 (`deliveryNudge.pct`). */
  pct: number;
  /** Why the approver approved ahead of delivery — required, kept on the line. */
  reason: string;
}

/**
 * DOES APPROVING THIS LINE TRIP THE DELIVERY-% NUDGE? — the one pure decision the
 * nudge turns on (#483). The board's config is on when `nudge.on`; the line trips it
 * when the delivered share is strictly below the bar. A bar of 0 never trips (nothing
 * is below 0%), and config absent or off never trips. Share is clamped defensively.
 * This decides the warning only — it NEVER blocks the approval.
 */
export function deliveryNudgeTrips(
  nudge: { on: boolean; pct: number } | undefined,
  share: number,
): boolean {
  if (!nudge || !nudge.on) return false;
  const bar = Math.max(0, Math.min(100, nudge.pct)) / 100;
  const got = Math.max(0, Math.min(1, share));
  return bar > 0 && got < bar;
}

/** The decision-tier governance headlines that sit above the ledger, all in the
 *  base currency. These read ONCE, on the governance line, never per row. */
export interface GovernanceRollup {
  /** Sum of lines approved but not yet paid — cleared to pay, money not moved. */
  approvedNotPaid: number;
  /** Sum of received lines not yet accepted — waiting on someone to verify work. */
  awaitingAcceptance: number;
  /** Retention held back: the retention rate applied to the paid (actual) spend. */
  retentionHeld: number;
  /** How many lines carry a control exception (duplicate or same-person). */
  exceptions: number;
}

/**
 * Roll the ledger up into the governance headlines. Retention is a headline only
 * in v1: `retentionHeld` is the board's retention rate applied to what has been
 * paid (there is no per-line retention schedule). Every money figure is in the
 * base currency, converted by the same rule as the budget rollup.
 */
export function governanceRollup(
  project: Pick<Project, 'invoices' | 'retentionRate'>,
  ctx?: BudgetContext,
): GovernanceRollup {
  const lines = project.invoices ?? [];
  const base = ctx?.base ?? '';
  let approvedNotPaid = 0;
  let awaitingAcceptance = 0;
  let paidTotal = 0;
  for (const l of lines) {
    const amt = Number.isFinite(l.amount) ? lineBaseAmount(l, base, ctx?.rates) : 0;
    if (l.status === 'approved') approvedNotPaid += amt;
    else if (l.status === 'received') awaitingAcceptance += amt;
    if (l.status === 'paid') paidTotal += amt;
  }
  const rate = project.retentionRate;
  const retentionHeld = rate && rate > 0 ? Math.round((rate / 100) * paidTotal) : 0;
  return {
    approvedNotPaid,
    awaitingAcceptance,
    retentionHeld,
    exceptions: controlExceptionIds(lines).size,
  };
}

/**
 * THE RETENTION PAY CEILING — while a project holds a retention rate, some of the
 * contract must always stay withheld, so the money paid out can never reach the
 * full budget. The ceiling is the approved budget less the retention on it
 * (`rate% of budget`): pay past it and nothing is being withheld, which defeats
 * the retention. With no retention rate there is no ceiling here. Every figure is
 * in the base currency; the budget baseline is authored in the base and paidTotal
 * is converted line-by-line, so they compare directly.
 */
export interface RetentionCeiling {
  /** The retention rate as a percent (0 when none is set). */
  rate: number;
  /** The approved budget baseline (base currency, 0 if unset). */
  budget: number;
  /** The amount that must stay withheld: rate% of the budget. */
  retentionAmount: number;
  /** The most that may ever be paid out: budget − retentionAmount. Infinity when
   *  no retention rate is set (this rule does not cap payment then). */
  payCeiling: number;
  /** What has already been paid (base). */
  paidTotal: number;
  /** How much may still be paid before the ceiling is reached (may be negative
   *  if historical payments already sit above it). Infinity when uncapped. */
  headroom: number;
}

export function retentionCeiling(
  project: Pick<Project, 'invoices' | 'retentionRate' | 'budget'>,
  ctx?: BudgetContext,
): RetentionCeiling {
  const lines = project.invoices ?? [];
  const base = ctx?.base ?? '';
  const rate = project.retentionRate && project.retentionRate > 0 ? project.retentionRate : 0;
  const budget = project.budget ?? 0;
  let paidTotal = 0;
  for (const l of lines) {
    if (l.status === 'paid') paidTotal += Number.isFinite(l.amount) ? lineBaseAmount(l, base, ctx?.rates) : 0;
  }
  const retentionAmount = rate > 0 ? Math.round((rate / 100) * budget) : 0;
  const payCeiling = rate > 0 ? Math.max(0, budget - retentionAmount) : Infinity;
  return { rate, budget, retentionAmount, payCeiling, paidTotal, headroom: payCeiling - paidTotal };
}

/**
 * Whether paying a line of `lineBase` (its base-currency amount) would breach the
 * retention ceiling — i.e. push total paid past `budget − retention`. Returns the
 * overage so the block can say by how much. A tiny epsilon absorbs float drift so
 * a payment that lands exactly on the ceiling is allowed. Uncapped when no rate.
 */
export function retentionPayBlock(
  ceiling: Pick<RetentionCeiling, 'rate' | 'payCeiling' | 'paidTotal'>,
  lineBase: number,
): { blocked: boolean; over: number } {
  if (ceiling.rate <= 0 || !Number.isFinite(ceiling.payCeiling)) return { blocked: false, over: 0 };
  const over = ceiling.paidTotal + lineBase - ceiling.payCeiling;
  return { blocked: over > 0.5, over: Math.max(0, over) };
}

/**
 * Read the retention-rate field into the value to store. A BLANK field returns
 * null — the field is cleared, retention lifted; whatever is left (0, a non-number,
 * or a negative) returns 0 — an explicit "no retention". A positive number returns
 * itself. It never returns undefined, because undefined would be dropped by the
 * board patch and the previous rate would silently persist: the bug this fixes,
 * where retention could be set but never cleared or zeroed.
 */
export function readRetentionRate(input: string): number | null {
  const t = input.trim();
  if (t === '') return null;
  // Keep the sign so a negative reads as ≤ 0 (→ 0), while stray units like "%" strip away.
  const n = Number(t.replace(/[^0-9.-]/g, ''));
  return Number.isFinite(n) && n > 0 ? n : 0;
}

/**
 * The member list after somebody is let in or shut out.
 *
 * THE AWKWARD CASE IS TAKING ACCESS AWAY FROM AN OPEN PROJECT. "Everyone" is
 * stored as an empty list, so there is no list to remove a name from — and
 * leaving it empty would mean the untick did nothing at all. The only honest
 * reading is: everyone who has access today, except this person. That converts
 * the project from open to a named list, which is a real change and is said so
 * on the screen rather than discovered later.
 *
 * It has a consequence worth knowing: somebody added to the workspace
 * afterwards is NOT on that list, so a project stops admitting new people the
 * moment it stops admitting anybody. That is what choosing means.
 *
 * Admins are never written into the list — they are allowed by rank, and
 * naming them would only make the list wrong when somebody stops being one.
 */
export function membersAfter(
  current: string[] | undefined,
  everyone: string[],
  email: string,
  letIn: boolean
): string[] {
  const e = email.trim().toLowerCase();
  const now = (current ?? []).map((m) => m.trim().toLowerCase()).filter(Boolean);
  if (letIn) {
    // Already open to all: leave it that way rather than pinning today's list.
    if (now.length === 0) return [];
    return now.includes(e) ? now : [...now, e];
  }
  const from = now.length === 0 ? everyone.map((m) => m.trim().toLowerCase()) : now;
  return from.filter((m) => m !== e);
}

/**
 * Whether this person may open this project.
 *
 * Being on the workspace list is still required and checked elsewhere; this is
 * the narrower question of whether a project has been reserved for particular
 * people. Both have to be true.
 */
export function canOpenProject(
  project: Pick<Project, 'members' | 'groups' | 'sample'>,
  email: string,
  isAdmin: boolean,
  /*
   * Which groups each person is in. Defaulted, because most callers ask about
   * a board with no groups on it and every existing caller predates them —
   * but NOT optional in meaning: leaving it out on a board that IS named on a
   * group answers "no" for somebody who can in fact open it, which would hide
   * the board from their own switcher.
   */
  groupsOf: GroupsOf = {}
): boolean {
  return canOpen(project, email, isAdmin, groupsOf);
}

/**
 * WHO MAY BE ASSIGNED WORK ON THIS BOARD — the people who can actually open it.
 *
 * The workspace list (`access.emails`) is wider than one board: a board can be
 * reserved for named people or for groups, and the whole workspace is not on it.
 * Offering that whole list as assignees let a card be handed to somebody who
 * could never see the board — assigned to a person who cannot open it, which is
 * the fault Vinnie found in quick-add. So this narrows the workspace list to
 * exactly those `canOpenProject` admits — the SAME test the board switcher uses,
 * so "who may be given this" and "who may open this" cannot drift apart. Each
 * candidate's own admin flag decides it (a workspace admin can open any board),
 * not the current user's.
 *
 * This decides only who may be CHOSEN. An assignee already ON a card is the
 * caller's to preserve even after they come off the board, so opening an old
 * issue never silently unassigns it.
 */
export function boardAssignees(
  project: Pick<Project, 'members' | 'groups'> | undefined,
  accessEmails: string[],
  admins: string[],
  groupsOf: GroupsOf = {}
): string[] {
  const adminSet = new Set(admins.map((a) => a.trim().toLowerCase()));
  const board = project ?? { members: [], groups: [] };
  return accessEmails.filter((email) =>
    canOpenProject(board, email, adminSet.has(email.trim().toLowerCase()), groupsOf)
  );
}

/**
 * THE ONE PERSON A BOARD BELONGS TO, when it belongs to exactly one.
 *
 * A board named to a single member is that person's board — every card on it is
 * theirs until somebody says otherwise. So a new card there starts assigned to
 * them rather than blank: a one-person board should never manufacture unowned
 * work, and "who has this" is never in doubt on it (the same reasoning as
 * `defaultAssignee` for an assistant-run board). Returns '' the moment there is
 * a choice to make — no members, or more than one — where a guess would be a
 * fact invented. This mirrors the sole-member attribution the accountability
 * roll-up already reads for display (accountability.ts), so the card is created
 * owned by the same person it would otherwise merely be attributed to.
 */
export function soleMember(project: Pick<Project, 'members'> | undefined): string {
  const members = (project?.members ?? []).map((m) => m.trim()).filter(Boolean);
  return members.length === 1 ? members[0] : '';
}

/**
 * WHO MAY OPEN A BOARD IS A LIST OF EMAIL ADDRESSES, AND NOTHING ELSE.
 *
 * THE FAULT THIS EXISTS FOR. The live PhotoSyncApp board had its member list
 * set to the single entry `Vinnie` — a name. `canOpenProject` above matches on
 * email address, so it matched nobody: the board was reserved for a person who
 * does not exist, and everybody except workspace admins was shut out of it.
 *
 * NOBODY TYPED IT IN. The People screen refuses anything without an @ and
 * always has. It arrived from `seed.json`, where `members` once meant the
 * TEAM — display names, from when the app tracked one board and the word had
 * no bearing on permission. The tracker push merged that list into the project
 * document on every run, and when `members` was later given its second meaning
 * the two collided on one field name.
 *
 * WHICH IS WHY THE CHECK IS HERE rather than on the input. A field can only
 * refuse what is typed into it. This is on the road out to the database, so a
 * script, an importer or a future screen cannot put a name in the list either
 * — and the value that caused it never came through a field at all.
 *
 * LOUD, NOT SILENT. It throws rather than dropping the bad entry, because the
 * failure mode of this list is already silence: a value that can never match
 * looks exactly like a board somebody else is using. Something refused with a
 * reason can be fixed; something quietly discarded is found weeks later.
 */
export function checkedMembers(members: string[]): string[] {
  const cleaned = members.map((m) => m.trim()).filter(Boolean);
  // Named as they were given, not as they were tidied. Somebody scanning their
  // list for what the message complained about is looking for `Vinnie`, and
  // being shown `vinnie` is one more thing to work out.
  const wrong = cleaned.filter((m) => !m.includes('@') || !m.includes('.'));
  if (wrong.length)
    throw new Error(
      `Who may open a board is a list of email addresses. ${wrong.join(', ')} ${wrong.length === 1 ? 'is not one' : 'are not'}, so ${wrong.length === 1 ? 'it' : 'they'} would match nobody and shut everyone out.`
    );
  // Lowercased only once it is known to be an address, because that is how
  // canOpenProject above compares them.
  return [...new Set(cleaned.map((m) => m.toLowerCase()))];
}

export const FIRST_PROJECT_ID = 'main';

export const CADENCE_LABEL: Record<'builds' | 'sprints', string> = {
  builds: 'Flow',
  sprints: 'Sprints',
};

export const CADENCE_BLURB: Record<'builds' | 'sprints', string> = {
  builds:
    'Work is pulled one item at a time — start the single most valuable thing next, with no fixed timebox. Suits a team that ships whenever something is ready.',
  sprints:
    'Work is planned into fixed stretches of time, and what did not finish rolls into the next. Suits a team that plans in fortnights.',
};

export const DELIVERY_MODE_LABEL: Record<'project' | 'continuous', string> = {
  project: 'A project',
  continuous: 'A continuous service',
};

export const DELIVERY_MODE_BLURB: Record<'project' | 'continuous', string> = {
  project:
    'A bounded piece of work with a start and an end. It has a plan behind it — phases, milestones and a Go-Live date — so the Plan tab and the project reporting are shown.',
  continuous:
    'Ongoing work with no fixed end, like a support queue or a maintenance board. There is no plan, contract or budget to track, so the Plan tab and the Finances group are hidden and the board follows the flow of work instead.',
};

/**
 * HOW URGENT, IN THE ORDER URGENCY RUNS. The label and the token, so a screen
 * never hard-codes the four words. Same set as a card's priority — #387.
 */
export const PROJECT_PRIORITIES: { value: ProjectPriority; label: string }[] = [
  { value: 'critical', label: 'Critical' },
  { value: 'high', label: 'High' },
  { value: 'medium', label: 'Medium' },
  { value: 'low', label: 'Low' },
];

/**
 * THE KINDS OF WORK AN ICT TEAM ACTUALLY DELIVERS — #387.
 *
 * WHY A LIST AND NOT FOUR BUTTONS. Vinnie, 9 September 2026: four fixed types
 * "aren't the only types that an ICT team can implement... We should not have
 * them so narrow." So this is a broad, searchable, grouped catalogue — and it
 * is a SEED, not a cage: the wizard also lets somebody name their own type, and
 * a type only ever seeds the starting phases, which are then editable. Grounded
 * in what monday, Teamwork and Smartsheet offer as project/work types.
 *
 * The type drives the phase template at creation (#377) and is otherwise just a
 * label on the project.
 */
export const PROJECT_TYPE_GROUPS: { group: string; types: string[] }[] = [
  {
    group: 'Software & applications',
    types: [
      'Custom software build',
      'Web application',
      'Mobile application',
      'API & integration',
      'Application upgrade',
    ],
  },
  {
    group: 'Data',
    types: ['Data migration', 'Data warehouse / BI', 'Reporting & analytics', 'Records digitisation'],
  },
  {
    group: 'Infrastructure & network',
    types: ['Server / infrastructure', 'Network rollout', 'Hardware refresh', 'Cabling / data centre'],
  },
  {
    group: 'Cloud',
    types: ['Cloud migration', 'SaaS implementation', 'Microsoft 365 / Workspace'],
  },
  {
    group: 'Security & compliance',
    types: ['Security implementation', 'Compliance / audit remediation', 'Backup & disaster recovery'],
  },
  {
    group: 'Enterprise systems',
    types: [
      'ERP implementation',
      'CRM implementation',
      'DocuWare implementation',
      'Document management (DMS)',
    ],
  },
  {
    group: 'Change & lifecycle',
    types: ['Process automation', 'System decommissioning', 'IT support / service rollout'],
  },
];

/** Every catalogue type, flat — for matching a typed value against the list. */
export const PROJECT_TYPES: string[] = PROJECT_TYPE_GROUPS.flatMap((g) => g.types);

/**
 * WHAT THE CREATE-PROJECT WIZARD COLLECTS — #387.
 *
 * A name and a key are required; everything else is optional, because "Start
 * blank" makes a project with only those two. The store's `createProject` takes
 * the whole draft, so a field added to the wizard is stored without touching the
 * seam in between.
 */
export interface ProjectDraft {
  name: string;
  key: string;
  description?: RichDoc;
  startDate?: number;
  goLive?: number;
  priority?: ProjectPriority;
  projectType?: string;
  budget?: number;
  fundingSource?: string;
  sponsor?: string;
  stakeholders?: Stakeholder[];
  risks?: string;
  successMeasure?: RichDoc;
  vendor?: string;
  scopeIn?: RichDoc;
  scopeOut?: RichDoc;
  projectManager?: string;
  assumptions?: RichDoc;
  constraints?: RichDoc;
  contractRef?: string;
  vendorContact?: string;
  /**
   * THE PHASES THE PROJECT STARTS WITH — #377. The wizard seeds these from the
   * type and lets them be reshaped before the board exists, so the edited set is
   * carried here and written by createProject. Absent (the two-field path, or
   * Start blank) means "seed the type's default template"; an empty array means
   * "the user cleared them" and none are written.
   */
  phases?: PhaseDraft[];
}

/** A short prefix, uppercase, no spaces — used to number this project's issues. */
export function tidyKey(raw: string): string {
  return raw.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 5);
}

/**
 * Suggests a key from a name, so nobody has to invent one.
 *
 * Takes the first LETTER of each word rather than the first character —
 * "PhotoSyncApp (Orbit)" has a word starting with a bracket, and using the raw
 * character then throwing away punctuation silently loses that word entirely.
 */
export function keyFromName(name: string): string {
  const words = name
    .trim()
    .split(/\s+/)
    .map((w) => w.replace(/[^A-Za-z0-9]/g, ''))
    .filter(Boolean);
  if (words.length === 0) return '';
  if (words.length === 1) return tidyKey(words[0].slice(0, 3));
  return tidyKey(words.map((w) => w[0]).join(''));
}

/**
 * Whether somebody may run this board regardless of their rank.
 *
 * Ownership only ever ADDS. A manager or an admin loses nothing by somebody
 * else owning a board — the checks that use this are always `rank OR owner`,
 * never `owner only`. Otherwise starting a board would be a way to lock the
 * people responsible for the workspace out of it.
 */
export function ownsProject(project: Pick<Project, 'owner'>, email: string): boolean {
  const owner = (project.owner ?? '').trim().toLowerCase();
  const me = (email ?? '').trim().toLowerCase();
  return !!owner && !!me && owner === me;
}

/**
 * A board id from its name — the document's own name in the database.
 *
 * VISIBLE NOWHERE, AND PERMANENT. Renaming a board changes its name; it cannot
 * change this, because every issue and every build lives at a path built from
 * it. So it is derived once and never again.
 *
 * The random tail is not decoration. Two people can reasonably start a board
 * called "Website" on the same day, and an id taken from the name alone would
 * have the second one land on top of the first — silently, because writing a
 * document that already exists is not an error.
 */
export function projectId(name: string, unique: string): string {
  const stem =
    name
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 24) || 'board';
  return `${stem}-${unique}`;
}

/** The names already taken, for the check below. Compared loosely on purpose. */
const same = (a: string, b: string) => a.trim().toLowerCase() === b.trim().toLowerCase();

/**
 * WHAT STOPS A BOARD BEING CREATED, in the words to show the person.
 *
 * The key is checked as hard as the name. It is stamped on every issue
 * reference on the board — #1 becomes ORB#1 wherever the two boards meet —
 * and two boards sharing one is a reference that no longer says which board it
 * belongs to. That cannot be corrected afterwards without rewriting every
 * issue, so it is worth refusing here.
 */
export function projectProblem(
  draft: { name: string; key: string },
  existing: Pick<Project, 'name' | 'key'>[]
): string {
  const name = draft.name.trim();
  const key = draft.key.trim();
  if (!name) return 'Give the board a name.';
  if (name.length > 60) return 'That name is too long to sit in the sidebar — keep it under 60 characters.';
  if (existing.some((p) => same(p.name, name))) return `There is already a board called ${name}.`;
  if (!key) return 'Give the board a short key. It goes in front of every issue number.';
  if (key.length < 2) return 'A key needs at least two characters.';
  if (existing.some((p) => same(p.key, key)))
    return `${key} is already used by ${existing.find((p) => same(p.key, key))!.name}. Two boards sharing a key makes an issue reference ambiguous.`;
  return '';
}

/**
 * The document a new board starts as.
 *
 * IT NAMES ITS OWNER AND NOBODY ELSE. A board created with an empty member
 * list would be open to the whole workspace the moment it existed — every
 * other tester could read it, and the person who made it would have published
 * their trial run without being asked. Starting closed and opening it later is
 * a decision; starting open is an accident.
 *
 * IT IS NOT MARKED AS SET UP. `setUpAt` is deliberately absent, so the board
 * offers the setup questions the first time it is opened rather than this form
 * asking all eight of them up front. Two things to type, then the board.
 */
export function newProjectDoc(opts: {
  id: string;
  name: string;
  key: string;
  owner: string;
  now: number;
  /*
   * The rest of what the wizard captured, if anything. Absent for "Start
   * blank" and for the two-field path that predates the wizard.
   */
  def?: Omit<ProjectDraft, 'name' | 'key'>;
}): Project & { owner: string; members: string[] } {
  const owner = opts.owner.trim().toLowerCase();
  const doc: Project & { owner: string; members: string[] } = {
    id: opts.id,
    name: opts.name.trim(),
    key: tidyKey(opts.key),
    nextNumber: 1,
    owner,
    members: [owner],
    source: 'board',
    sourceName: '',
    repoUrl: '',
    createdAt: opts.now,
  };
  /*
   * ONLY WHAT WAS ACTUALLY GIVEN IS WRITTEN. A blank definition field left on
   * the document would be an empty column where null means "unset", and a
   * "Start blank" project would arrive carrying a row of empty strings that
   * every "is this set?" check then has to second-guess.
   */
  const def = opts.def ?? {};
  if (richHasText(def.description)) doc.description = def.description;
  if (typeof def.startDate === 'number') doc.startDate = def.startDate;
  if (typeof def.goLive === 'number') doc.goLive = def.goLive;
  if (def.priority) doc.priority = def.priority;
  if (def.projectType?.trim()) doc.projectType = def.projectType.trim();
  if (typeof def.budget === 'number') doc.budget = def.budget;
  if (def.fundingSource?.trim()) doc.fundingSource = def.fundingSource.trim();
  if (def.sponsor?.trim()) doc.sponsor = def.sponsor.trim();
  if (def.stakeholders?.length) doc.stakeholders = def.stakeholders.filter((s) => s.name?.trim());
  if (def.risks?.trim()) doc.risks = def.risks.trim();
  if (richHasText(def.successMeasure)) doc.successMeasure = def.successMeasure;
  if (def.vendor?.trim()) doc.vendor = def.vendor.trim();
  // Scope, assumptions and constraints are rich text (0113): a wizard/import may
  // seed them, and richHasText drops an empty document so a blank field stays unset.
  if (richHasText(def.scopeIn)) doc.scopeIn = def.scopeIn;
  if (richHasText(def.scopeOut)) doc.scopeOut = def.scopeOut;
  if (def.projectManager?.trim()) doc.projectManager = def.projectManager.trim();
  if (richHasText(def.assumptions)) doc.assumptions = def.assumptions;
  if (richHasText(def.constraints)) doc.constraints = def.constraints;
  if (def.contractRef?.trim()) doc.contractRef = def.contractRef.trim();
  if (def.vendorContact?.trim()) doc.vendorContact = def.vendorContact.trim();
  return doc;
}

/** Whether a board names nobody, and so admits the whole workspace. */
export function openToEveryone(project: Pick<Project, 'members' | 'groups'>): boolean {
  // BOTH lists. A board reserved for a group is not open, and saying it is
  // would publish one team's work to the whole workspace on the screen while
  // the database quite correctly refused it.
  return (
    (project.members ?? []).filter((m) => m.trim()).length === 0 &&
    (project.groups ?? []).filter((g) => g.trim()).length === 0
  );
}

/**
 * WHAT UNTICKING WILL ACTUALLY DO, said before it is done.
 *
 * Taking one person off a board that is open to everyone does not simply
 * remove them — there is no list to remove them from. It converts the board
 * from open to a named list of everybody who has access today, minus them.
 *
 * That has a consequence nobody would guess from a tick box: the board stops
 * admitting people added to the workspace afterwards. Somebody invited next
 * week will not be on that list, and will find themselves shut out of a board
 * that was open to all until this moment.
 */
export function accessChangeWarning(
  project: Pick<Project, 'members' | 'name'>,
  letIn: boolean
): string {
  if (letIn || !openToEveryone(project)) return '';
  return `${project.name} is open to everyone in the workspace. Taking one person off pins it to the people who have access today — anybody added later will not be on it.`;
}

/**
 * ONE PERSON'S BOARDS, SPLIT INTO THE TWO THAT MATTER.
 *
 * WHY NOT THE GRID. Everybody against every board is the whole truth and the
 * wrong shape to act on. At twenty boards it is three screens of sideways
 * scrolling and two hundred and forty tick boxes, and the question somebody
 * actually arrives with — "what can this person reach, and what can they do
 * there?" — is answered by reading across all three screens.
 *
 * Down the page, one person at a time, it is a list. Lists do not run out of
 * width, so this shape holds at twenty boards and at two hundred.
 *
 * ON FIRST, because that is the shorter list and the one being checked. A new
 * starter has two boards out of twenty and the useful screen opens with those
 * two at the top, not with the eighteen they are not on.
 */
export function boardsForPerson(opts: {
  projects: Project[];
  email: string;
  isAdmin: boolean;
  /** Narrows by board name or key. Empty shows everything. */
  filter?: string;
  /** Which groups each person is in — a board may admit them through one. */
  groupsOf?: GroupsOf;
}): { on: Project[]; off: Project[] } {
  const { projects, email, isAdmin, filter = '', groupsOf = {} } = opts;
  const q = filter.trim().toLowerCase();
  const matches = (p: Project) =>
    !q || p.name.toLowerCase().includes(q) || p.key.toLowerCase().includes(q);
  const on: Project[] = [];
  const off: Project[] = [];
  for (const p of projects) {
    if (!matches(p)) continue;
    (canOpenProject(p, email, isAdmin, groupsOf) ? on : off).push(p);
  }
  return { on, off };
}

/** "On 4 of 20 boards" — the answer before any reading. */
export function accessSummary(on: number, total: number, isAdmin: boolean): string {
  if (isAdmin) return `Every board — ${total} of ${total}. Admins are never shut out of one.`;
  if (total === 0) return 'There are no boards yet.';
  if (on === 0) return `On none of the ${total} boards.`;
  if (on === total) return `On all ${total} boards.`;
  return `On ${on} of ${total} boards.`;
}
